import React, {
  memo, useContext, useEffect, useRef,
} from 'react'

import {
  Button, Close, Icon, Spinner,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'
import {
  arrayOf, bool, func, number, shape, string,
} from 'prop-types'

import { iconSize } from 'Pods/ThreeDStudio/constants'
import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'
import ThreeDStudioPageContext from 'Pods/ThreeDStudio/ThreeDStudioPageContext'

import LikerRow from './Components/LikerRow/LikerRow'
import formatLikedAt from './Utils/formatLikedAt'

// The list of users who liked the opened model, anchored above the footer like
// control. Non-modal: it closes on outside click (Triangle's dropdown) and on
// Escape (the trigger). Focus enters on open but is not trapped - Tab can leave.
function LikersPopover({
  likers, totalCount, hasMore = false, isLoading = false, onLoadMore, onClose,
}) {
  const t = useTranslation()
  const { locale } = useContext(ThreeDStudioPageContext)
  const containerRef = useRef(null)
  const listRef = useRef(null)
  const sentinelRef = useRef(null)

  const title = t('likers.title', { count: totalCount })

  // Triangle's dropdown does no focus handling, and `usePortal` moves this out of
  // the trigger's tab order - so without this the close button below is reachable
  // only by tabbing the whole document. Focus lands on the container rather than
  // the button so the dialog label is announced first; preventScroll keeps the
  // portaled popover from scrolling the viewer underneath it.
  useEffect(() => {
    containerRef.current?.focus({ preventScroll: true })
  }, [])

  // Infinite scroll over the paginated list: fetch the next page when the
  // sentinel nears the fold of the list's own scroll container - against the
  // viewport the overflow clip would cancel the rootMargin prefetch. onLoadMore
  // guards itself against re-entry, so a re-observe after an append is a no-op
  // when nothing should happen.
  useEffect(() => {
    const sentinel = sentinelRef.current

    if (!sentinel || typeof IntersectionObserver === 'undefined') {
      return undefined
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          onLoadMore()
        }
      },
      { root: listRef.current, rootMargin: '120px', threshold: 0 },
    )

    observer.observe(sentinel)

    return () => observer.disconnect()
  }, [hasMore, onLoadMore])

  return (
    <div className="likers-popover" role="dialog" aria-label={title} tabIndex={-1} ref={containerRef}>
      <div className="likers-popover__header">
        <span className="likers-popover__title cgt-body-md-semibold">{title}</span>
        <Button
          type="ghost"
          size="small"
          variant="only-icon"
          actionType="button"
          icon={<Icon svgComponent={Close} size={iconSize} notSpaced />}
          aria-label={t('likers.close')}
          onClick={onClose}
          customClass="likers-popover__close"
        />
      </div>
      <div className="likers-popover__body" ref={listRef}>
        <ul className="likers-popover__list">
          {likers.map(liker => (
            <LikerRow
              key={liker.id}
              displayName={liker.displayName}
              username={liker.username}
              avatarUrl={liker.avatarUrl}
              timeLabel={formatLikedAt(liker.createdAt, { t, locale })}
            />
          ))}
        </ul>
        {hasMore && <div className="likers-popover__sentinel" ref={sentinelRef} aria-hidden="true" />}
        {isLoading && (
          <div className="likers-popover__loader">
            <Spinner size="small" />
          </div>
        )}
      </div>
    </div>
  )
}

LikersPopover.propTypes = {
  likers: arrayOf(shape({
    id: number,
    createdAt: string,
    username: string,
    displayName: string,
    avatarUrl: string,
  })).isRequired,
  totalCount: number.isRequired,
  hasMore: bool,
  isLoading: bool,
  onLoadMore: func.isRequired,
  onClose: func.isRequired,
}

export default memo(LikersPopover)
